import {
  asFiles,
  asNumber,
  asRange,
  asString,
  asStringArray,
  parseDice,
  type CollectionData,
  type FieldDef,
  type FieldType,
  type RecordData,
} from '@tomo/core';
import { Avatar, Button, Calendar, ColorDot, IconButton, Input, SearchList, type SearchItem } from '@tomo/ui';
import { Image as ImageIcon, Plus, X } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { ingestFile } from '../../data/blobs.ts';
import { useAllRecords, useCollections, usePeople, useProject } from '../../data/hooks.tsx';
import { pickFiles } from '../../lib/files.ts';
import { Icon } from '../../lib/icons.tsx';
import { AssetThumb } from '../assets/AssetThumb.tsx';

/** Edited in place with a text input. */
export const INLINE_TYPES: ReadonlySet<FieldType> = new Set(['text', 'url', 'number', 'dice']);
/** Edited by interacting with the rendered value itself. */
export const DIRECT_TYPES: ReadonlySet<FieldType> = new Set(['checkbox', 'rating', 'votes']);
/** Computed or managed elsewhere. */
export const READONLY_TYPES: ReadonlySet<FieldType> = new Set(['formula']);

export const isPopoverType = (t: FieldType) => !INLINE_TYPES.has(t) && !DIRECT_TYPES.has(t) && !READONLY_TYPES.has(t);

/* ---------------------------------------------------------------------------------------------- *
 * Inline text / number editor
 * ---------------------------------------------------------------------------------------------- */

export function InlineEditor({
  record,
  field,
  onDone,
  className,
  autoFocus = true,
  size = 'sm',
}: {
  record: RecordData;
  field: FieldDef;
  onDone: () => void;
  className?: string;
  autoFocus?: boolean;
  size?: 'sm' | 'md';
}) {
  const project = useProject();
  const raw = record.values[field.id];
  const initial = field.type === 'number' ? (asNumber(raw)?.toString() ?? '') : asString(raw);
  const [value, setValue] = useState(initial);
  const cancelled = useRef(false);
  const commit = () => {
    if (cancelled.current) return;
    const v = value.trim();
    if (field.type === 'number') {
      const n = v === '' ? null : asNumber(v);
      if (v !== '' && n === null) return;
      if (n !== asNumber(raw)) project.setValue(record.id, field.id, n);
    } else if (v !== initial) project.setValue(record.id, field.id, v);
  };
  const dice = field.type === 'dice' ? parseDice(value) : null;
  return (
    <div className={className} style={{ position: 'relative' }}>
      <Input
        size={size}
        autoFocus={autoFocus}
        inputMode={field.type === 'number' ? 'decimal' : undefined}
        type={field.type === 'url' ? 'url' : 'text'}
        placeholder={field.type === 'dice' ? '2d6+3' : field.type === 'url' ? 'https://' : field.name}
        value={value}
        invalid={(field.type === 'number' && value.trim() !== '' && asNumber(value) === null) || (field.type === 'dice' && value.trim() !== '' && !dice)}
        trailing={
          field.type === 'number' && field.config.suffix ? field.config.suffix.trim() : dice ? <span className="tabular">{dice.min}–{dice.max}</span> : undefined
        }
        onFocus={(e) => e.currentTarget.select()}
        onChange={(e) => setValue(e.target.value)}
        onBlur={() => {
          commit();
          onDone();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            e.currentTarget.blur();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            cancelled.current = true;
            onDone();
          }
        }}
      />
    </div>
  );
}

/* ---------------------------------------------------------------------------------------------- *
 * Popover editors
 * ---------------------------------------------------------------------------------------------- */

export interface PopoverEditorProps {
  record: RecordData;
  field: FieldDef;
  collection: CollectionData;
  onDone: () => void;
}

export function PopoverEditor(props: PopoverEditorProps) {
  switch (props.field.type) {
    case 'select':
    case 'multiSelect':
      return <OptionEditor {...props} />;
    case 'person':
      return <PersonEditor {...props} />;
    case 'relation':
      return <RelationEditor {...props} />;
    case 'date':
      return <DateEditor {...props} />;
    case 'range':
      return <RangeEditor {...props} />;
    case 'color':
      return <ColorEditor {...props} />;
    case 'file':
      return <FileEditor {...props} />;
    default:
      return null;
  }
}

function OptionEditor({ record, field, collection, onDone }: PopoverEditorProps) {
  const project = useProject();
  const multi = field.type === 'multiSelect';
  const selected = multi ? asStringArray(record.values[field.id]) : asString(record.values[field.id]) ? [asString(record.values[field.id])] : [];
  const items: SearchItem[] = (field.config.options ?? []).map((o) => ({ id: o.id, label: o.label, icon: <ColorDot color={o.color} size={9} /> }));
  const toggle = (id: string) => {
    if (multi) {
      const next = selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id];
      project.setValue(record.id, field.id, next);
    } else {
      project.setValue(record.id, field.id, selected[0] === id ? null : id);
      onDone();
    }
  };
  return (
    <SearchList
      items={items}
      selectedIds={selected}
      showCheck
      placeholder={multi ? 'Find or create tags…' : 'Find or create an option…'}
      emptyText="No options yet"
      onSelect={(it) => toggle(it.id)}
      onCreate={(label) => {
        const id = project.addSelectOption(collection.id, field.id, label);
        toggle(id);
      }}
      createLabel={(q) => (
        <>
          Create <strong>{q}</strong>
        </>
      )}
      onEscape={onDone}
      maxHeight={280}
    />
  );
}

function PersonEditor({ record, field, onDone }: PopoverEditorProps) {
  const project = useProject();
  const people = usePeople();
  const multi = field.config.multiple !== false;
  const selected = asStringArray(record.values[field.id]);
  const items: SearchItem[] = people.map((p) => ({
    id: p.id,
    label: p.name,
    icon: <Avatar name={p.name} color={p.color} size={18} />,
    hint: p.sample ? 'Sample' : project.actorId === p.id ? 'You' : undefined,
  }));
  return (
    <SearchList
      items={items}
      selectedIds={selected}
      showCheck
      placeholder="Find people…"
      emptyText="No teammates yet — invite them from a Pro workspace."
      onSelect={(it) => {
        if (multi) project.setValue(record.id, field.id, selected.includes(it.id) ? selected.filter((x) => x !== it.id) : [...selected, it.id]);
        else {
          project.setValue(record.id, field.id, selected[0] === it.id ? null : [it.id]);
          onDone();
        }
      }}
      onEscape={onDone}
    />
  );
}

function RelationEditor({ record, field, onDone }: PopoverEditorProps) {
  const project = useProject();
  const all = useAllRecords();
  const collections = useCollections();
  const target = field.config.targetCollectionId ? collections.find((c) => c.id === field.config.targetCollectionId) : undefined;
  const multi = field.config.multiple !== false;
  const selected = asStringArray(record.values[field.id]);
  const byCollection = useMemo(() => new Map(collections.map((c) => [c.id, c])), [collections]);
  const items: SearchItem[] = useMemo(
    () =>
      all
        .filter((r) => r.id !== record.id && (!target || r.collectionId === target.id))
        .map((r) => {
          const c = byCollection.get(r.collectionId);
          const file = r.cover;
          return {
            id: r.id,
            label: r.title || 'Untitled',
            icon: file ? <AssetThumb file={file} style={{ width: 18, height: 18, borderRadius: 4 }} /> : <Icon token={r.icon ?? c?.icon} size={15} />,
            group: target ? undefined : c?.name,
            hint: target ? undefined : undefined,
          };
        }),
    [all, record.id, target, byCollection, project],
  );
  const toggle = (id: string) => {
    if (multi) project.setValue(record.id, field.id, selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
    else {
      project.setValue(record.id, field.id, selected[0] === id ? null : [id]);
      onDone();
    }
  };
  return (
    <SearchList
      items={items}
      selectedIds={selected}
      showCheck
      placeholder={target ? `Link ${target.name.toLowerCase()}…` : 'Link any record…'}
      emptyText={target ? `No ${target.name.toLowerCase()} yet` : 'No records yet'}
      onSelect={(it) => toggle(it.id)}
      onCreate={
        target
          ? (title) => {
              const id = project.createRecord(target.id, { title });
              toggle(id);
            }
          : undefined
      }
      createLabel={(q) => (
        <>
          New {target?.titleLabel.toLowerCase() ?? 'record'} <strong>{q}</strong>
        </>
      )}
      onEscape={onDone}
      maxHeight={300}
    />
  );
}

function DateEditor({ record, field, onDone }: PopoverEditorProps) {
  const project = useProject();
  return (
    <Calendar
      value={asString(record.values[field.id]) || null}
      onChange={(v) => {
        project.setValue(record.id, field.id, v);
        onDone();
      }}
    />
  );
}

function RangeEditor({ record, field, onDone }: PopoverEditorProps) {
  const project = useProject();
  const [a0, b0] = asRange(record.values[field.id]);
  const [a, setA] = useState(a0?.toString() ?? '');
  const [b, setB] = useState(b0?.toString() ?? '');
  const save = () => {
    const lo = asNumber(a);
    const hi = asNumber(b);
    project.setValue(record.id, field.id, lo === null && hi === null ? null : [lo ?? hi, hi ?? lo]);
    onDone();
  };
  return (
    <form
      className="range-editor"
      onSubmit={(e) => {
        e.preventDefault();
        save();
      }}
    >
      <Input size="sm" autoFocus placeholder="Min" inputMode="decimal" value={a} onChange={(e) => setA(e.target.value)} />
      <span className="subtle">–</span>
      <Input size="sm" placeholder="Max" inputMode="decimal" value={b} onChange={(e) => setB(e.target.value)} />
      <Button size="sm" variant="primary" type="submit">
        Save
      </Button>
    </form>
  );
}

const HEX_SWATCHES = ['#E5484D', '#F76B15', '#FFC53D', '#46A758', '#12A594', '#0090FF', '#3E63DD', '#8E4EC6', '#D6409F', '#AD7F58', '#8B8D98', '#1C2024', '#FFFFFF', '#FFE58F', '#B3D3FF', '#B6EBC3'];

function ColorEditor({ record, field, onDone }: PopoverEditorProps) {
  const project = useProject();
  const current = asString(record.values[field.id]);
  const [hex, setHex] = useState(current);
  const set = (v: string | null) => {
    project.setValue(record.id, field.id, v);
    onDone();
  };
  return (
    <div className="color-editor">
      <div className="color-editor__grid">
        {HEX_SWATCHES.map((c) => (
          <button key={c} type="button" className="color-editor__swatch" aria-pressed={current.toLowerCase() === c.toLowerCase()} style={{ background: c }} onClick={() => set(c)} aria-label={c} />
        ))}
      </div>
      <form
        className="color-editor__row"
        onSubmit={(e) => {
          e.preventDefault();
          if (/^#[0-9a-f]{3,8}$/i.test(hex.trim())) set(hex.trim());
        }}
      >
        <Input size="sm" leading={<span className="value-color__swatch" style={{ background: /^#[0-9a-f]{3,8}$/i.test(hex) ? hex : 'transparent' }} />} value={hex} placeholder="#hex" onChange={(e) => setHex(e.target.value)} />
        <input type="color" aria-label="Pick color" value={/^#[0-9a-f]{6}$/i.test(hex) ? hex : '#000000'} onChange={(e) => setHex(e.target.value)} className="color-editor__native" />
        <Button size="sm" variant="ghost" onClick={() => set(null)}>
          Clear
        </Button>
      </form>
    </div>
  );
}

/**
 * Files on a record: add any number, remove any of them. Images can also be made the record's
 * cover from here, which is the one picture a record carries without a field.
 */
function FileEditor({ record, field }: PopoverEditorProps) {
  const project = useProject();
  const [busy, setBusy] = useState(false);
  const files = asFiles(record.values[field.id]);

  const add = async () => {
    const picked = await pickFiles({ multiple: true });
    if (!picked.length) return;
    setBusy(true);
    try {
      const metas = await Promise.all(picked.map((f) => ingestFile(f, f.name)));
      project.setValue(record.id, field.id, [...files, ...metas]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="file-editor">
      {files.length > 0 && (
        <ul className="file-editor__list">
          {files.map((f, i) => (
            <li key={`${f.hash}:${i}`} className="file-editor__row">
              <AssetThumb file={f} className="file-editor__thumb" />
              <span className="g-truncate" title={f.name}>
                {f.name}
              </span>
              {f.mime.startsWith('image/') && record.cover?.hash !== f.hash && (
                <IconButton label="Use as cover" size="xs" onClick={() => project.setCover(record.id, f)}>
                  <ImageIcon />
                </IconButton>
              )}
              <IconButton
                label={`Remove ${f.name}`}
                size="xs"
                onClick={() => project.setValue(record.id, field.id, files.filter((_, j) => j !== i))}
              >
                <X />
              </IconButton>
            </li>
          ))}
        </ul>
      )}
      <Button size="sm" variant="ghost" icon={<Plus />} loading={busy} onClick={add}>
        Add files
      </Button>
    </div>
  );
}
